import React from 'react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { useToolResultContent } from './ToolResultDataContext';
import { PdfArtifactCard } from './PdfArtifactCard';

import { useTranslation } from 'react-i18next';

interface PrintResult {
  pdfUrl?: string;
  fileName?: string;
}

/**
 * Renders a printFile tool call result.
 * Shows a spinner while rendering, then a PDF download card.
 * Uses content.id for lookup so it works regardless of tool name.
 */
export const PrintResultBlock: React.FC<{ content: ToolUseBlock; isStreaming: boolean }> = ({
  content,
  isStreaming,
}) => {
  const { t } = useTranslation('pleri/chat');
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const isPending = useChatStore((s) =>
    activeConversationId
      ? (s.conversations[activeConversationId]?.pendingToolUses ?? new Set()).has(content.id)
      : false,
  );
  const resultJson = useToolResultContent(content.id);

  if (isStreaming || isPending || !resultJson) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
        <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {t('renderingPdf')}
      </div>
    );
  }

  let result: PrintResult;
  try {
    result = JSON.parse(resultJson);
  } catch {
    return null;
  }

  const { pdfUrl, fileName } = result;
  if (!pdfUrl || !fileName) return null;

  return <PdfArtifactCard url={pdfUrl} fileName={fileName} />;
};
